/**
 * Pure helpers for the Who spoke panel: evidence line, talk-time sort,
 * conflict-peer lookup.
 */

import type { ISpeakerSuggestion } from "@lexicanon/shared"
import type { I18n } from "@lingui/core"
import { type ISpeakerVM, speakerDisplayLabel } from "../../lib/speaker-vm"

/** Join suggestion evidence details with " · ", skipping empty details. */
export function buildEvidenceLine(evidence: ISpeakerSuggestion["evidence"] | undefined): string {
  if (!evidence || evidence.length === 0) return ""
  const parts: string[] = []
  for (const item of evidence) {
    if (typeof item.detail === "string" && item.detail.length > 0) parts.push(item.detail)
  }
  return parts.join(" · ")
}

/** Sort by talk share descending; unnamed rows keep whatever rank that gives them. */
export function sortSpeakersByTalk(speakers: readonly ISpeakerVM[]): ISpeakerVM[] {
  return [...speakers].sort((a, b) => {
    const pa = a.talkPercent ?? -1
    const pb = b.talkPercent ?? -1
    if (pa !== pb) return pb - pa
    return a.slot - b.slot
  })
}

/**
 * The other diarisation label that matched the same voice, if any. Prefers this
 * speaker's own suggestion; otherwise a peer whose suggestion points at us
 * (so an unnamed row can still show the conflict).
 */
export function conflictWithLabelFor(speaker: ISpeakerVM, all: readonly ISpeakerVM[]): string | null {
  const own = speaker.suggestion?.conflictWithLabel
  if (typeof own === "string" && own.length > 0) return own
  if (speaker.label === null) return null
  for (const other of all) {
    if (other === speaker) continue
    if (other.suggestion?.conflictWithLabel === speaker.label) return other.label
  }
  return null
}

/** User-facing label for a conflict peer ("Speaker 2"), never a raw Speaker_S3. */
export function conflictDisplayLabel(conflictWithLabel: string, speakers: readonly ISpeakerVM[], i18n: I18n): string {
  const peer = speakers.find((s) => s.label === conflictWithLabel)
  if (peer) return speakerDisplayLabel(peer, i18n)
  return conflictWithLabel.replace(/_/g, " ").replace(/^Remote::/, "")
}

export function needsAName(speaker: ISpeakerVM): boolean {
  return speaker.status === "unidentified" || speaker.status === "suggested"
}

function nameKey(name: string): string {
  return name.trim().toLowerCase()
}

/**
 * Other named/confirmed speakers that share this display name. Empty when the
 * speaker is still unnamed or the name is unique.
 */
export function duplicateNamePeers(speaker: ISpeakerVM, all: readonly ISpeakerVM[]): ISpeakerVM[] {
  if (needsAName(speaker)) return []
  const key = nameKey(speaker.displayName)
  if (!key) return []
  return all.filter((other) => other !== speaker && !needsAName(other) && nameKey(other.displayName) === key)
}

export function sampleDurationSeconds(window: { start: number; end: number } | null | undefined): number {
  if (!window) return 0
  return Math.max(1, Math.round(window.end - window.start))
}

/** Cap a listen-target at 6 s so a long monologue doesn't become a 3-minute "sample". */
export const SAMPLE_WINDOW_MAX_SECONDS = 6

export function clipSampleWindow(
  window: { start: number; end: number } | null | undefined,
  maxSeconds = SAMPLE_WINDOW_MAX_SECONDS,
): { start: number; end: number } | null {
  if (!window) return null
  const duration = window.end - window.start
  if (!(duration > 0)) return null
  if (duration <= maxSeconds) return window
  return { start: window.start, end: window.start + maxSeconds }
}
